import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Shield, CheckCircle2, Clock, Play, Loader2, Scale, Activity } from 'lucide-react';
import {
  getEngineReviewList,
  getEngineReviewListExternal,
  runInitialEngines,
} from '../../../services/engineReviewApi';
import { ReviewStatusPill, FinalizeModal } from './';
import {
  ENGINE_NAMES,
  ENGINE_DESCRIPTIONS,
  type EngineId,
  type EngineReviewListResponse,
  type ReviewPanelMode,
} from '../../../types/engineReview';

export interface EngineReviewLandingContentProps {
  mode: ReviewPanelMode;
  reportId?: string;
  showHeader?: boolean;
  hrefForEngine?: (engineId: EngineId) => string;
}

const ENGINE_ORDER: EngineId[] = [
  'merchant_reconciliation',
  'merchant_revenue_augmentation',
  'bottom_up',
  'enhanced_claim',
  'dual_method',
  'mitigation',
  'impact',
  'epi',
  'policy_deductions',
  'pv',
  'fair_settlement',
  'case_strength',
  // Independent first-party property engine — the backend already includes it in
  // the list response and surfaces it as its own attestable card. It does NOT
  // gate finalize (excluded from attestable_total), and its detail view renders
  // a graceful placeholder for BI-only claims with no parsed property loss.
  'property',
  // Methodology-loop + Group A deterministic engines. The backend list
  // endpoint iterates the full engine_dag.ENGINES so these already come back
  // in the response; none of them gate finalize (excluded from
  // ATTESTABLE_ENGINE_IDS).
  'projection_trend',
  'two_period',
  'capacity_ceiling',
  'non_recurring_item_detector',
  'catch_up_sales',
  'monthly_limit_cap',
  'extra_expense_cap',
  'third_party_damages',
];

export function EngineReviewLandingContent({
  mode,
  reportId: reportIdProp,
  showHeader = true,
  hrefForEngine,
}: EngineReviewLandingContentProps) {
  const { reportId: routeReportId } = useParams<{ reportId: string }>();
  const reportId = reportIdProp ?? routeReportId;
  const navigate = useNavigate();
  const location = useLocation();

  // Forensic Assistant deep-link forward. The chat's "Review & apply" button
  // navigates to this landing route (it knows the reportId, not which engine
  // page hosts the override modal). The override modal only lives on the
  // per-engine in-house EDIT page, so when an `overrideSuggestion` arrives
  // here, forward it to that engine's edit route — re-passing the suggestion
  // and replacing history so a back/refresh doesn't re-trigger the redirect.
  useEffect(() => {
    const suggestion = (location.state as any)?.overrideSuggestion;
    if (suggestion && reportId && mode !== 'external') {
      navigate(
        `/dashboard/accountant/review/${reportId}/${suggestion.engineId}/edit`,
        { replace: true, state: { overrideSuggestion: suggestion } },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, reportId, mode]);

  const [data, setData] = useState<EngineReviewListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether the last fetch threw (e.g. 404) vs a clean empty-engines
  // response. Used to preserve the PR 240 engine-grid fallback for 404s while
  // showing the unavailable message for a clean { engines: [] / undefined } payload.
  const [fetchErrored, setFetchErrored] = useState(false);
  const [running, setRunning] = useState(false);
  const [runFeedback, setRunFeedback] = useState<
    | { kind: 'success'; dispatchId: string; status: string; autoApproved: boolean }
    | { kind: 'error'; message: string }
    | null
  >(null);
  // Local cooldown: post-trigger we disable the button for 60s so a user
  // can't spam-click during the 1-3 min Celery worker turnaround. Pure
  // UX guard — the orchestrator is idempotent on the backend either way.
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  // External-attester finalize. The backend 412-gates finalize on an
  // unacknowledged acknowledgment-required verifier WARNING; FinalizeModal is
  // the surface that lets the attester see + acknowledge it (CP 04 11).
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalized, setFinalized] = useState(false);

  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return;
    }
    fetchEngines();
  }, [reportId, mode]);

  // Tick once a second while in cooldown so the disabled button releases
  // automatically. Effect short-circuits when not in cooldown.
  useEffect(() => {
    if (!cooldownUntil) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [cooldownUntil]);

  const fetchEngines = async () => {
    setLoading(true);
    setFetchErrored(false);
    try {
      // Mode-aware list dispatch. External hits the witness-JWT-gated
      // endpoint shipped in 07.3n; in-house keeps the Clerk-gated path.
      const fetchFn =
        mode === 'external' ? getEngineReviewListExternal : getEngineReviewList;
      const result = await fetchFn(reportId!);
      setData(result);
    } catch (err: any) {
      // API 404 / empty state: no reviews started yet. Render the
      // 12-engine grid with all engines as `unreviewed` rather than
      // blocking with an error card — a fresh report legitimately has
      // no review records.
      setFetchErrored(true);
      setData({
        report_id: reportId!,
        engines: [],
        all_approved: false,
        finalize_enabled: false,
        attestable_total: 0,
        attestable_approved: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const backPath = mode === 'external'
    ? `/accountant/reports/${reportId}`
    : '/dashboard/accountant';

  const detailPath = (engineId: EngineId) =>
    mode === 'external'
      ? `/accountant/review/${reportId}/${engineId}`
      : `/dashboard/accountant/review/${reportId}/${engineId}`;

  const resolveHref = hrefForEngine ?? detailPath;

  // The "Run engines" button is gated to in-house mode (`data_specialist`
  // role on the backend) and disabled while:
  //   * a request is in flight,
  //   * a 60s post-trigger cooldown is still ticking, OR
  //   * any engine is currently `under_review` — letting a fresh run
  //     stomp partial review state would be confusing for the reviewer.
  const cooldownRemaining =
    cooldownUntil && cooldownUntil > now ? Math.ceil((cooldownUntil - now) / 1000) : 0;
  // Guard against a degraded API payload where `engines` is undefined at runtime.
  const engines = data?.engines ?? [];
  const anyUnderReview = engines.some((e) => e.status === 'under_review');
  const runDisabled = running || cooldownRemaining > 0 || anyUnderReview;

  const handleRunInitial = async () => {
    if (!reportId || runDisabled) return;
    setRunning(true);
    setRunFeedback(null);
    try {
      const res = await runInitialEngines(reportId);
      setRunFeedback({
        kind: 'success',
        dispatchId: res.dispatch_id,
        status: res.status,
        autoApproved: res.auto_approved,
      });
      setCooldownUntil(Date.now() + 60_000);
      // Kick a refresh in 5s so newly-written engine_outputs rows surface
      // even before the worker finishes the full 12-engine walk.
      setTimeout(() => fetchEngines(), 5_000);
    } catch (err: any) {
      setRunFeedback({
        kind: 'error',
        message: err?.message || 'Failed to queue engine pipeline.',
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }} data-testid="engine-review-landing">
      {showHeader && (
        <>
          {/* Header */}
          <div className="px-6 py-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
            <div className="max-w-7xl mx-auto">
              <button
                onClick={() => navigate(backPath)}
                className="cs-meta flex items-center gap-2 mb-3 transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-text-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; }}
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="cs-display">
                    Engine Review
                  </h1>
                  <p className="cs-meta mt-1">
                    Review and attest to each calculation engine's output
                  </p>
                </div>
                {data && (
                  <div className="flex items-center gap-4">
                    <div className="cs-body flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                      <span>{data.attestable_approved} of {data.attestable_total} approved</span>
                    </div>
                    {/* In-house entry to the Methodology Comparison view — surfaces
                        the cited BI/property loss-method slate the engine grid omits.
                        In-house only (the methodology endpoint is Clerk/org-scoped). */}
                    {mode !== 'external' && (
                      <Link
                        to={`/dashboard/accountant/methodology/${reportId}`}
                        data-testid="open-methodology-view"
                        title="Compare every loss methodology this claim supports"
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors"
                        style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-surface)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
                      >
                        <Scale className="w-3.5 h-3.5" />
                        Methodology
                      </Link>
                    )}
                    {/* Analysis Tools — Monte Carlo + sensitivity, the defend-the-number
                        companion to Methodology. In-house only (Clerk/org-scoped). */}
                    {mode !== 'external' && (
                      <Link
                        to={`/dashboard/accountant/analysis/${reportId}`}
                        data-testid="open-analysis-tools"
                        title="Stress-test this number: confidence interval + driver sensitivity"
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors"
                        style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', background: 'var(--color-bg-surface)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-bg-surface)'; }}
                      >
                        <Activity className="w-3.5 h-3.5" />
                        Stress Test
                      </Link>
                    )}
                    {data.finalize_enabled && (
                      <span
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1 rounded-full border"
                        style={{ background: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' }}
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Ready to finalize
                      </span>
                    )}
                    {/* In-house only: the witness/external mode uses the
                        rerun-per-engine flow already shipped. */}
                    {mode !== 'external' && (
                      <button
                        type="button"
                        onClick={handleRunInitial}
                        disabled={runDisabled}
                        data-testid="run-initial-engines"
                        title={
                          anyUnderReview
                            ? 'Disabled while an engine is under review'
                            : cooldownRemaining > 0
                            ? `Recently triggered — try again in ${cooldownRemaining}s`
                            : 'Run all engines now'
                        }
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', borderColor: 'var(--color-brand)' }}
                        onMouseEnter={(e) => { if (!runDisabled) e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                      >
                        {running ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Queueing…
                          </>
                        ) : cooldownRemaining > 0 ? (
                          <>
                            <Clock className="w-3.5 h-3.5" />
                            Cooling down ({cooldownRemaining}s)
                          </>
                        ) : (
                          <>
                            <Play className="w-3.5 h-3.5" />
                            Run engines
                          </>
                        )}
                      </button>
                    )}
                    {/* External attester: the actual finalize action. Opens the
                        modal that surfaces + collects acknowledgment of any
                        ack-required verifier WARNING (CP 04 11 grounding). */}
                    {mode === 'external' && data.finalize_enabled && !finalized && (
                      <button
                        type="button"
                        onClick={() => setFinalizeOpen(true)}
                        data-testid="open-finalize-modal"
                        title="Review verifier status and sign off on this report"
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-colors"
                        style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', borderColor: 'var(--color-brand)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
                      >
                        <Shield className="w-3.5 h-3.5" />
                        Finalize attestation
                      </button>
                    )}
                    {finalized && (
                      <span
                        data-testid="finalized-badge"
                        className="cs-chip inline-flex items-center gap-1.5 px-3 py-1 rounded-full border"
                        style={{ background: 'transparent', color: 'var(--color-verified)', borderColor: 'var(--color-verified)' }}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Attested
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Run-engines feedback banner (in-house only). Surfaces the
              Celery task id for support traceability + an error string when
              the dispatch fails (worker not subscribed, role gate failure, etc.). */}
          {runFeedback && mode !== 'external' && (
            <div
              data-testid={`run-initial-feedback-${runFeedback.kind}`}
              className="max-w-7xl mx-auto px-6 pt-4"
            >
              <div
                className="cs-body rounded-md border px-4 py-2"
                style={
                  runFeedback.kind === 'success'
                    ? { background: 'transparent', borderColor: 'var(--color-verified)', color: 'var(--color-verified)' }
                    : { background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }
                }
              >
                {runFeedback.kind === 'success' ? (
                  <>
                    {runFeedback.autoApproved
                      ? 'Engine pipeline queued. Outputs will appear within ~1–3 minutes.'
                      : `Engine pipeline dispatch created — ${runFeedback.status}. Approve on the dispatch queue dashboard to start the run.`}
                    {` (dispatch: ${runFeedback.dispatchId})`}
                  </>
                ) : (
                  <>Failed to queue engine pipeline: {runFeedback.message}</>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {ENGINE_ORDER.map((id) => (
              <div
                key={id}
                className="rounded-[2px] border p-5 animate-pulse"
                style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              >
                <div className="h-5 w-24 rounded mb-3" style={{ background: 'var(--color-bg-elevated)' }} />
                <div className="h-4 w-full rounded mb-2" style={{ background: 'var(--color-bg-hover)' }} />
                <div className="h-4 w-3/4 rounded" style={{ background: 'var(--color-bg-hover)' }} />
              </div>
            ))}
          </div>
        )}

        {!loading && data && !fetchErrored && engines.length === 0 && (
          <p
            className="cs-body text-center py-12"
            style={{ color: 'var(--color-text-muted)' }}
            data-testid="engines-unavailable"
          >
            No engine outputs to review yet
          </p>
        )}

        {!loading && data && (fetchErrored || engines.length > 0) && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            data-testid="engine-card-grid"
          >
            {ENGINE_ORDER.map((engineId) => {
              const engine = engines.find((e) => e.engine_id === engineId);
              const status = engine?.status ?? 'unreviewed';

              return (
                <Link
                  key={engineId}
                  to={resolveHref(engineId)}
                  className="rounded-[2px] border p-5 transition-all group"
                  style={{ background: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; }}
                  data-testid={`engine-card-${engineId}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="cs-h3">
                      {ENGINE_NAMES[engineId]}
                    </h3>
                    {/* Fixed dimensions + single-line (K3 P2): every status chip
                        renders at one consistent size regardless of label length
                        ("Not yet reviewed" vs "Approved"), and flex-shrink-0 stops
                        this flex-between row from squeezing the chip into a wrap.
                        Scoped to this call site only — ReviewStatusPill is shared
                        by CalculationReviewPanel/UpstreamProvenance, which keep
                        their own sizing. */}
                    <ReviewStatusPill
                      status={status}
                      size="sm"
                      className="flex-shrink-0 justify-center whitespace-nowrap min-w-[132px]"
                    />
                  </div>
                  <p className="cs-meta leading-relaxed">
                    {ENGINE_DESCRIPTIONS[engineId]}
                  </p>
                  {engine && engine.overrides_count > 0 && (
                    <div className="cs-meta mt-3 flex items-center gap-1.5" style={{ color: 'var(--color-needs-human-text)' }}>
                      <Clock className="w-3.5 h-3.5" />
                      {engine.overrides_count} active override{engine.overrides_count !== 1 ? 's' : ''}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {reportId && (
        <FinalizeModal
          isOpen={finalizeOpen}
          onClose={() => setFinalizeOpen(false)}
          reportId={reportId}
          onFinalized={() => {
            setFinalizeOpen(false);
            setFinalized(true);
          }}
        />
      )}
    </div>
  );
}


export default EngineReviewLandingContent;
